/**
 * Shared search/ranking utilities.
 * Used by ProgrammeBuilder (via filteredElements) and SlideManager (via searchAndRankSlides).
 */

export const normalizeText = (s) =>
  (s == null ? '' : String(s))
    .replace(/[\u2018\u2019\u201A\u201B\u02BC\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"');

export const stripPunctuation = (s) =>
  s.replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();

/**
 * Split a search string into lower-case terms and their punctuation-stripped equivalents.
 * @param {string} searchTerm
 * @returns {{ terms: string[], stripped: string[] }}
 */
export const parseSearchTerms = (searchTerm) => {
  const terms = normalizeText(searchTerm || '')
    .toLowerCase()
    .split(/[\s,]+/)
    .map(s => s.trim())
    .filter(Boolean);
  return {
    terms,
    stripped: terms.map(stripPunctuation),
  };
};

/**
 * Score a set of fields against a list of search terms.
 *
 * @param {Array<{ text: string, stripped: string, weight: number }>} fields
 *   Each field's text should already be lower-cased and normalizeText'd;
 *   stripped should be the result of stripPunctuation(text).
 * @param {string[]} searchTerms   Lower-cased, normalised terms.
 * @param {string[]} searchTermsStripped  Punctuation-stripped versions of searchTerms.
 * @returns {object|null}  Score object, or null if no terms matched at all.
 */
export const buildSearchScore = (fields, searchTerms, searchTermsStripped) => {
  if (!searchTerms.length) {
    return { weightedHits: 0, termHits: 0, sequenceBonus: 0, punctuationBonus: 0, extraChars: 0, totalPosition: 0, textLength: 0 };
  }

  let weightedHits = 0;
  let termHits = 0;
  let punctuationBonus = 0;
  let extraChars = 0;
  let textLength = 0;
  let totalPosition = 0;

  for (let i = 0; i < searchTerms.length; i++) {
    const term = searchTerms[i];
    const termStripped = searchTermsStripped[i];
    if (!termStripped) continue;

    const matches = fields
      .map(field => {
        const pos = field.stripped.indexOf(termStripped);
        return pos >= 0 ? { ...field, pos } : null;
      })
      .filter(Boolean);

    if (!matches.length) continue;

    termHits += 1;

    if (fields.some(f => f.text.includes(term))) punctuationBonus += 1;

    matches.sort((a, b) => {
      if (a.weight !== b.weight) return b.weight - a.weight;
      if (a.pos !== b.pos) return a.pos - b.pos;
      return a.stripped.length - b.stripped.length;
    });

    const best = matches[0];
    weightedHits += best.weight;
    extraChars += Math.max(0, best.stripped.length - termStripped.length);
    textLength += best.stripped.length;
    totalPosition += best.pos;
  }

  if (termHits === 0) return null;

  // Sequence bonus: all terms appear contiguously in order within a single field
  const strippedTermsForSeq = searchTermsStripped.filter(Boolean);
  let sequenceBonus = 0;
  if (strippedTermsForSeq.length > 1) {
    outer: for (const field of fields) {
      let startSearch = 0;
      while (startSearch <= field.stripped.length) {
        const firstPos = field.stripped.indexOf(strippedTermsForSeq[0], startSearch);
        if (firstPos < 0) break;
        let cursor = firstPos + strippedTermsForSeq[0].length;
        let ok = true;
        for (let t = 1; t < strippedTermsForSeq.length; t++) {
          while (cursor < field.stripped.length && /\s/.test(field.stripped[cursor])) cursor++;
          if (!field.stripped.startsWith(strippedTermsForSeq[t], cursor)) { ok = false; break; }
          cursor += strippedTermsForSeq[t].length;
        }
        if (ok) { sequenceBonus = 1; break outer; }
        startSearch = firstPos + 1;
      }
    }
  }

  return { weightedHits, termHits, sequenceBonus, punctuationBonus, extraChars, totalPosition, textLength };
};

/**
 * Compare two score objects. Returns negative if a ranks higher, positive if b ranks higher.
 */
export const compareScores = (a, b) => {
  if (a.sequenceBonus !== b.sequenceBonus) return b.sequenceBonus - a.sequenceBonus;
  if (a.punctuationBonus !== b.punctuationBonus) return b.punctuationBonus - a.punctuationBonus;
  if (a.weightedHits !== b.weightedHits) return b.weightedHits - a.weightedHits;
  if (a.totalPosition !== b.totalPosition) return a.totalPosition - b.totalPosition;
  if (a.termHits !== b.termHits) return b.termHits - a.termHits;
  if (a.extraChars !== b.extraChars) return a.extraChars - b.extraChars;
  return a.textLength - b.textLength;
};

/**
 * Build pre-processed field entries from a raw value + weight.
 */
const makeField = (value, weight) => {
  const text = normalizeText(value == null ? '' : String(value)).toLowerCase().trim();
  if (!text) return null;
  return { text, stripped: stripPunctuation(text), weight };
};

/**
 * Search and rank a flat array of slide objects.
 *
 * Fields searched:
 *   - name       weight 10
 *   - file       weight 1
 *   - flags[].name  weight 3
 *
 * Returns all slides unchanged when searchTerm is empty/blank.
 */
export const searchAndRankSlides = (slides, searchTerm) => {
  if (!Array.isArray(slides)) return [];

  const { terms, stripped } = parseSearchTerms(searchTerm);
  if (!terms.length) return slides;

  const scored = slides
    .map((slide, index) => {
      const fields = [
        makeField(slide.name, 10),
        makeField(slide.file, 1),
        makeField(slide.slide_text, 1),
        ...( Array.isArray(slide.flags)
          ? slide.flags.map(f => makeField(f?.name, 3))
          : []
        ),
      ].filter(Boolean);

      const score = buildSearchScore(fields, terms, stripped);
      return score ? { slide, score, index } : null;
    })
    .filter(Boolean);

  scored.sort((a, b) => {
    const cmp = compareScores(a.score, b.score);
    return cmp !== 0 ? cmp : a.index - b.index;
  });

  return scored.map(e => e.slide);
};
